import faker from 'faker';
import { sum } from 'lodash';
import {
  Box,
  Grid,
  Card,
  Table,
  Divider,
  TableRow,
  Container,
  TableBody,
  TableHead,
  TableCell,
  Typography,
  TableContainer,
  Stack,
  Button,
  TextField,
  OutlinedInput,
  Tooltip,
  Paper
} from '@material-ui/core';
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fCurrency } from 'src/utils/formatNumber';
import {
  getLaborTimelogCost,
  getRecipeCostLaborRowComputed,
  resolveInstallLaborServiceUnitCost,
  resolveInstallLaborServiceLineCost,
  resolveJobCostServiceLineTotal,
  isJobCostInstallLaborPayout,
  isJobCostInstallLaborTimelog,
  sumJobCostProductionTimelogLabor,
  includeJobCostServiceRowActual,
  includeJobCostMaterialRowActual,
  getJobCostServiceActualCost,
  resolveEffectiveProductionLabor
} from 'src/utils/laborTimelogCalc';
import {
  getJobCostMaterialCost,
  getCompareExpectedMaterialQty,
  getCompareExpectedMaterialUnitCost,
  includeMaterialOnCompareExpectedBudget
} from 'src/utils/jobCostMaterialCalc';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import { LoadingButton } from '@material-ui/lab';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import { empty } from 'src/Helpers/Common';
import { forwardRef } from 'react';
import ReactToPrint from 'react-to-print';
import { useTheme } from '@emotion/react';
import useCompanyProfile from 'src/hooks/useCompanyProfile';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import { Print } from "@material-ui/icons";
import { GetApp } from "@material-ui/icons";
import JobCostChartSignwise from 'src/components/charts/JobCostChartSignwise';
import JobCostNotesPage from 'src/components/charts/JobCostNotesPage';



// Styled Components
const RowResultStyle = styled(TableRow)(({ theme }) => ({
  '& td': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  }
}));

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: '100%',
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`
  }
}));

const getOtherEntriesTotals = (entries = []) => {
  const list = Array.isArray(entries) ? entries : [];
  return list.reduce(
    (acc, entry) => {
      const qty = Number(entry?.quantity ?? 0);
      const unitCost = Number(entry?.unit_cost ?? 0);
      const unitBill = Number(entry?.unit_bill ?? 0);
      const rowCostRaw = Number(entry?.total_cost);
      const rowBillRaw = Number(entry?.total_bill);
      const billable = entry?.billable === 1 || entry?.billable === '1' || entry?.billable === true;

      const rowCost = Number.isFinite(rowCostRaw) ? rowCostRaw : (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unitCost) ? unitCost : 0);
      const rawBill = Number.isFinite(rowBillRaw) ? rowBillRaw : (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unitBill) ? unitBill : 0);
      const rowBill = billable ? rawBill : 0;

      acc.cost += rowCost;
      acc.bill += rowBill;
      return acc;
    },
    { cost: 0, bill: 0 }
  );
};

/** Include job-cost service rows in totals when DB has qty/cost or is billable/taken */
const includeRecipeCostServiceRow = (service) => {
  if (!service) return false;
  if (service.taken === 1 || service.taken === '1') return true;
  if (parseFloat(service.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(service.jobcost_total_cost || service.total_cost || 0) > 0) return true;
  if (parseFloat(service.qty || 0) > 0) return true;
  if (parseFloat(service.total_bill || 0) > 0) return true;
  if (parseFloat(service.jobcost_total_bill || 0) > 0) return true;
  return false;
};

const includeRecipeCostMaterialRow = (material) => {
  if (!material) return false;
  if (material.taken === 1 || material.taken === '1') return true;
  if (parseFloat(material.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(material.jobcost_total || 0) > 0) return true;
  if (parseFloat(material.quantity || 0) > 0) return true;
  if (parseFloat(material.total_bill || 0) > 0) return true;
  if (parseFloat(material.jobcost_total_bill || 0) > 0) return true;
  return false;
};

const skipServiceOnExpectedBudgetList = (service) =>
  Number(service?.from_job) === 1 &&
  Number(service?.labour_cost_service_id) !== 10 &&
  service?.recipe_id == null;

/** T&M job-cost materials are actual-only; never belong on Expected budget. */
// Backup before T&M expected material edit fix: relied on notFromRecipe + jobcost_actual_quantity > 0 only (T&M materials leaked into Expected after edit).
// Backup before non-T&M formula expected fix: Number(material.jobcost_actual_quantity || 0) > 0 — missed recipe materials with quantity only.
const includeMaterialOnExpectedBudget = (material, isTMProject = false) =>
  includeMaterialOnCompareExpectedBudget(material, isTMProject);

// Backup before T&M expected material edit fix: .filter((curr) => curr.notFromRecipe != true)
// Backup before T&M sales order material expected fix:
// const includeMaterialOnExpectedPayout = (material, isTMProject = false) => {
//   if (!material || isTMProject) return false;
//   return material.notFromRecipe != true;
// };
const includeMaterialOnExpectedPayout = (material, isTMProject = false) => {
  if (!material) return false;
  if (material.notFromRecipe == true) return false;
  if (isTMProject) {
    const expectedQty = Number(material?.jobcost_actual_quantity ?? material?.quantity ?? 0);
    return expectedQty > 0;
  }
  return true;
};

const getChartExpectedServiceUnitCost = (service, isTMProject = false) => {
  if (!service) return 0;
  // T&M Install Labor graph expected cost should use markup catalog rate.
  // Backup before T&M graph expected Install Labor markup fix:
  // return Number(service.originalCost || service.unit_cost || 0);
  if (isTMProject && Number(service.labour_cost_service_id) === 10) {
    return resolveInstallLaborServiceUnitCost(service, true);
  }
  return Number(service.originalCost || service.unit_cost || 0);
};

const includeRecipeCostStockRow = (stock) => {
  if (!stock) return false;
  if (stock.taken === 1 || stock.taken === '1') return true;
  if (parseFloat(stock.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(stock.jobcost_total_cost || 0) > 0) return true;
  if (parseFloat(stock.location_quantity || 0) > 0) return true;
  if (parseFloat(stock.total_bill || 0) > 0) return true;
  if (parseFloat(stock.jobcost_total_bill || 0) > 0) return true;
  return false;
};

export default function CompareJobCostChartMain({ id, tmLabel, logs, setLogs, fetchJobCostLogs, requestId, recalculateTrigger, staffTimelogsProp, laborTabLaborCost = 0 }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const componentRef = useRef();
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedProjects1, setSelectedProjects1] = useState([]);
  const [materialPayOut, setMaterialPayOut] = useState(0);
  const [materialPayOut1, setMaterialPayOut1] = useState(0);
  const [manualTotalPayOut, setManualTotalPayOut] = useState(0);
  const [hourlyTotalPayOut, setHourlyTotalPayOut] = useState(0);
  const [excessHourlyPayoutAmount, setExcessHourlyPayoutAmount] = useState(0)
  const [excessManualPayoutAmount, setExcessManualPayoutAmount] = useState(0)
  const [additionalServices, setAdditionalServices] = useState([])
  const [projectData, setProjectData] = useState([])
  const [recipe, setRecipe] = useState([])
  // const [services, setServices] = useState([]);
  const [services, setAllServices] = useState([]);
  const [otherEntries, setOtherEntries] = useState([]);
  const [staffTimelogsGet, setStaffTimelogsGet] = useState([]);
  const timelogsForCalc = useMemo(
    () => (staffTimelogsProp != null ? staffTimelogsProp : staffTimelogsGet),
    [staffTimelogsProp, staffTimelogsGet]
  );
  const [allPayouts, setAllPayouts] = useState([]);
  const [manuallyAddedPayouts, setManuallyAddedPayouts] = useState([]);
  const [stockProducts, setStockProducts] = useState([])
  const [jobCost, setJobCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialMaterials, setInitialMaterials] = useState([]);
  const [productList, setProductList] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const params = localStorage.getItem("params") || requestId
  const user = JSON.parse(window.localStorage.getItem('user'))

  const [data, setData] = useState({});
  const fetchDetails = async () => {
    try {
      const res = await axios.post(`${REST_API_END_POINT}settings/get-companyProfile-details`);
      if (res.data.status === 1) {
        setData(...res.data.data);
      }
    } catch (error) {
      console.log('get-companyProfile-details error : ', error);
    }
  };

  const fetchFinishedProductsData = async () => {
    try {
      const finishedProductsResponse = await axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-add-materials-finished-products`);
      return Array.isArray(finishedProductsResponse?.data?.results)
        ? finishedProductsResponse.data.results
        : [];
    } catch (error) {
      console.error('Error fetching finished products:', error);
      return [];
    }
  };

  const fetchData = () => {
    try {
      setLoading(true)
      // Backup (before product-formula compare fix — mapped with empty productList on first load):
      // const jobCostRequest = axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-recipe`,
      //   { userId: user.user_id, userType: user.user_type, projectId: id, orderId: requestId || params });
      // jobCostRequest.then((response) => {
      //   const productListData = Array.isArray(productList) ? productList : [];
      //   fetchFinishedProductsData()
      //     .then((freshProductListData) => {
      //       if (Array.isArray(freshProductListData) && freshProductListData.length > 0) {
      //         setProductList(freshProductListData);
      //       }
      //     })
      //     .catch((error) => console.error('Error refreshing finished products:', error));
      Promise.all([
        axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-recipe`,
          { userId: user.user_id, userType: user.user_type, projectId: id, orderId: requestId || params }),
        fetchFinishedProductsData()
      ]).then(([response, freshProductListData]) => {
          const productListData = Array.isArray(freshProductListData) && freshProductListData.length > 0
            ? freshProductListData
            : (Array.isArray(productList) ? productList : []);
          if (productListData.length > 0) {
            setProductList(productListData);
          }
          let selectedProducts = response.data.selectedMaterials
          let receivedRfpProducts = selectedProducts.filter(row => row.rfp === 1)
          let rfpProducts = receivedRfpProducts.map(product => ({
            ...product,
            jobcost_unit_cost: product.unit_cost,
            jobcost_quantity: product.quantity,
            jobcost_actual_unit_cost: product.actual_unit_cost,
            jobcost_actual_quantity: product.actual_quantity
          }));
          selectedProducts = selectedProducts.filter(row => row.rfp === 0)
          let initialData = response.data.selectedMaterials
          const allPayouts = response.data.allPayOut
          const project = response.data.projects
          const allMaterials = response.data.allMaterials
          const allRecipes = response.data.allRecipies
          const material = response.data.materials
          let allServices = response.data.allServices
          let stockProducts = response?.data?.stockProducts
          const fetchedOtherEntries = response?.data?.otherEntries || [];
          if (response?.data?.newstafftimelogs) {
            setStaffTimelogsGet(response.data.newstafftimelogs);
          }
          let additionalServices = response.data.additionalServices
          if (additionalServices) {
            setAdditionalServices(additionalServices.reduce((accumulator, currentValue) => {
              return accumulator + (currentValue?.jobcost_unit_cost * currentValue?.jobcost_quantity);
            }, 0))
          }
          if (project) {
            setProjectData(project)
          }
          if (allRecipes) {
            setRecipe(allRecipes)
          }
          if (allServices) {
            setAllServices(allServices)
          }
          setOtherEntries(fetchedOtherEntries);
          if (stockProducts) {
            setStockProducts(stockProducts)
          }
          if (allPayouts?.length > 0) {
            let totalPayOut = allPayouts?.reduce((acc, curr) => acc + curr.amount, 0)
            const totalHourlyPayoutAmount = allPayouts?.filter(payout => payout.is_auto_pay === 1 && (payout.service_id === null || payout.service_id === undefined || payout.service_id === 0 || payout.service_id === "0") && payout.is_manually_added !== 1)?.reduce((acc, curr) => acc + curr.amount, 0);
            setExcessHourlyPayoutAmount(totalHourlyPayoutAmount)
            const totalManualPayoutAmount = allPayouts?.filter(payout => payout.is_auto_pay !== 1 && (payout.service_id === null || payout.service_id === undefined || payout.service_id === 0 || payout.service_id === "0") && payout.is_manually_added !== 1)?.reduce((acc, curr) => acc + curr.amount, 0);
            setExcessManualPayoutAmount(totalManualPayoutAmount)

            const summedHourlyPayouts = Object.values(
              allPayouts?.filter(payout => payout.is_auto_pay === 1)?.reduce((acc, payout) => {
                const key = `${payout.project_id}-${payout.task_id || "null"}-${payout.team || "null"}`;

                if (!acc[key]) {
                  acc[key] = {
                    project_id: payout.project_id,
                    task_id: payout.task_id,
                    team: payout.team,
                    total_amount: 0,
                  };
                }

                acc[key].total_amount = parseFloat((acc[key].total_amount + payout.amount).toFixed(2));

                return acc;
              }, {})
            );

            console.log('summedHourlyPayouts===', summedHourlyPayouts);

            const summedManualPayouts = Object.values(
              allPayouts?.filter(payout => payout.is_auto_pay !== 1)?.reduce((acc, payout) => {
                const key = `${payout.project_id}-${payout.task_id || "null"}-${payout.team || "null"}`;

                if (!acc[key]) {
                  acc[key] = {
                    project_id: payout.project_id,
                    task_id: payout.task_id,
                    team: payout.team,
                    total_amount: 0,
                  };
                }

                acc[key].total_amount = parseFloat((acc[key].total_amount + payout.amount).toFixed(2));

                return acc;
              }, {})
            );

            console.log('summedManualPayouts===', summedManualPayouts);

            setHourlyTotalPayOut(summedHourlyPayouts?.reduce((acc, curr) => acc + curr.total_amount, 0))
            setManualTotalPayOut(summedManualPayouts?.reduce((acc, curr) => acc + curr.total_amount, 0))
          }
          if (allPayouts) {
            setAllPayouts(allPayouts || []);
            const fetchedIsTM = Number(project?.project_category) === 12 || project?.job_category_name === 'Time & Material';
            const manualPayouts = allPayouts.filter(
              // Backup before T&M production labor fix: p => (p.is_manually_added === 1 || p.is_manually_added === "1") && (p.service_id === null || p.service_id === undefined || p.service_id === 0 || p.service_id === "0" || !p.service_id)
              // Backup before Install Labor request-service classification:
              // p => (p.is_manually_added === 1 || p.is_manually_added === "1") &&
              //      (p.service_id === null || p.service_id === undefined || p.service_id === 0 || p.service_id === "0" || !p.service_id) &&
              //      (!fetchedIsTM || !isJobCostInstallLaborPayout(p))
              p => (p.is_manually_added === 1 || p.is_manually_added === "1") &&
                   (p.service_id === null || p.service_id === undefined || p.service_id === 0 || p.service_id === "0" || !p.service_id) &&
                   (!fetchedIsTM || !isJobCostInstallLaborPayout(p, services))
            );
            setManuallyAddedPayouts(manualPayouts || []);
          }
          if (initialData) {
            setInitialMaterials(initialData)
          }
          if (productListData) {
            setProductList(productListData)
          }
          if (allMaterials) {
            setMaterialList(allMaterials)
          }
          if (project) {
            setJobCost(project.cost || 0)
          }
          if (selectedProducts?.length > 0 || material?.length > 0) {
            const list = Array.isArray(productListData) ? productListData : [];
            const selectedProjects1 = (selectedProducts || []).map(selectedProduct => {
              const matchingProduct = productListData.find(product => product.id === selectedProduct.product_id);
              if (matchingProduct) {
                return {
                  ...matchingProduct,
                  product_id: selectedProduct.product_id,
                  jobcost_quantity: selectedProduct.quantity,
                  jobcost_unit_cost: selectedProduct.unit_price,
                  jobcost_total: selectedProduct.total,
                  jobcost_actual_quantity: selectedProduct.actual_quantity,
                  jobcost_actual_unit_cost: selectedProduct.actual_unit_price,
                  markup_percent: selectedProduct.markup_percent,
                  material_markup_percentage: selectedProduct.material_markup_percentage,
                  product_markup: selectedProduct.product_markup,
                  // Backup before T&M graph material fix: markup fallback came only from matchingProduct.
                  markup: selectedProduct.markup || selectedProduct.retail_price || matchingProduct?.markup,
                  retail_price: selectedProduct.retail_price || matchingProduct?.retail_price,
                  recipeName: selectedProduct.recipeName,
                  taken: selectedProduct.taken,
                  task_id: selectedProduct.task_id
                };
              }
              return null; // Handle cases where no matching product is found
            }).filter(project => project !== null);
            const selectedProjects2 = (material || []).map(selectedProduct => {
              const matchingProduct = productListData.find(product => product.id === selectedProduct.product_id);
              if (matchingProduct) {
                return {
                  ...matchingProduct,
                  product_id: selectedProduct.product_id,
                  jobcost_quantity: selectedProduct.quantity,
                  jobcost_unit_cost: selectedProduct.unit_price,
                  jobcost_total: selectedProduct.total,
                  jobcost_actual_quantity: selectedProduct.actual_quantity,
                  jobcost_actual_unit_cost: selectedProduct.actual_unit_price,
                  markup_percent: selectedProduct.markup_percent,
                  material_markup_percentage: selectedProduct.material_markup_percentage,
                  product_markup: selectedProduct.product_markup,
                  // Backup before T&M graph material fix: markup fallback came only from matchingProduct.
                  markup: selectedProduct.markup || selectedProduct.retail_price || matchingProduct?.markup,
                  retail_price: selectedProduct.retail_price || matchingProduct?.retail_price,
                  recipeName: selectedProduct.recipeName,
                  taken: selectedProduct.taken,
                  task_id: selectedProduct.task_id
                };
              }
              return null; // Handle cases where no matching product is found
            }).filter(project => project !== null);

            const orphanAsProjects = (material || [])
              .filter((m) => !list.some((p) => p.id === m.product_id))
              .map((m) => {
                const qty = Number(m.quantity) || 0;
                const actualQty = Number(m.actual_quantity ?? m.quantity) || 0;
                const unit = Number(m.unit_price) || 0;
                const actualUnit = Number(m.actual_unit_price ?? m.unit_price) || 0;
                return {
                  id: m.product_id,
                  name: m.productName || m.rfpProductName || 'Material',
                  product_id: m.product_id,
                  jobcost_quantity: qty,
                  jobcost_unit_cost: unit,
                  jobcost_total: parseFloat(m.total) || 0,
                  jobcost_actual_quantity: actualQty,
                  jobcost_actual_unit_cost: actualUnit,
                  markup_percent: m.markup_percent,
                  material_markup_percentage: m.material_markup_percentage,
                  product_markup: m.product_markup,
                  // Backup before T&M graph material fix: orphan material rows did not carry retail_price/markup.
                  markup: m.markup || m.retail_price || 0,
                  retail_price: m.retail_price,
                  recipeName: m.recipeName || '',
                  taken: m.taken,
                  task_id: m.task_id,
                  notFromRecipe: false,
                  billable: m.billable ?? 1
                };
              });

            const selectedProjects2Merged = [...(selectedProjects2 || []), ...orphanAsProjects];
            setSelectedProjects([...selectedProjects1, ...rfpProducts]);
            setSelectedProjects1(selectedProjects2Merged);

            // Backup before T&M category type fix: const fetchedIsTM = Number(project?.project_category) === 12;
            const fetchedIsTM = Number(project?.project_category) === 12 || project?.job_category_name === 'Time & Material';
            const materialPayOut = selectedProjects2Merged
              // Backup before T&M expected material edit fix: .filter((curr) => curr.notFromRecipe != true)
              .filter((curr) => includeMaterialOnExpectedPayout(curr, fetchedIsTM))
              .reduce(
                (acc, curr) =>
                  // Backup before T&M material markup: acc + (Number(curr.jobcost_actual_quantity) || 0) * (Number(curr.jobcost_actual_unit_cost) || 0)
                  acc + getJobCostMaterialCost(curr, fetchedIsTM, {
                    qty: curr.jobcost_actual_quantity,
                    unitCost: curr.jobcost_actual_unit_cost
                  }),
                0
              );

            const materialPayOut1 = selectedProjects2Merged
              // Backup before T&M sales order material actual fix:
              // .filter((curr) => curr.notFromRecipe != true)
              .filter((curr) => curr.notFromRecipe != true && includeJobCostMaterialRowActual(curr, false))
              // Backup before T&M material markup: .reduce((acc, curr) => acc + (Number(curr.jobcost_total) || 0), 0);
              .reduce((acc, curr) => acc + getJobCostMaterialCost(curr, fetchedIsTM), 0);

            console.log('Material PayOut (Not From Recipe):', materialPayOut);
            console.log('Material PayOut1 (From Recipe):', materialPayOut1);

            setMaterialPayOut(materialPayOut);
            setMaterialPayOut1(materialPayOut1);

          }
        })
    }
    catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 500);
    }
  }

  useEffect(() => {
    if (id) {
      console.log("sdfsdfsfd", requestId || params)
      fetchData()
    }
    fetchDetails()
  }, [id, requestId, params, recalculateTrigger]);

  // Backup before T&M material markup fix: const isTMProject = projectData?.project_category === 12;
  const isTMProject = Number(projectData?.project_category) === 12 || projectData?.job_category_name === 'Time & Material';
  const materialTotal = useMemo(() => {
    return (selectedProjects || []).reduce((sum, proj) => {
      // Backup before T&M material markup: return sum + (Number(proj.jobcost_actual_unit_cost || proj.unit_cost || 0) * Number(proj.jobcost_actual_quantity || 0));
      return sum + getJobCostMaterialCost(proj, isTMProject, {
        // Backup before non-T&M formula expected fix: qty: proj.jobcost_actual_quantity only.
        qty: getCompareExpectedMaterialQty(proj),
        unitCost: getCompareExpectedMaterialUnitCost(proj)
      });
    }, 0).toFixed(2);
  // Backup before T&M material markup: }, [selectedProjects]);
  }, [selectedProjects, isTMProject]);

  const materialTotal1 = useMemo(() => {
    return (selectedProjects || []).filter(i => (
      // Backup before T&M sales order material actual fix:
      // includeJobCostMaterialRowActual(i, isTMProject) ||
      // (isTMProject && (includeRecipeCostMaterialRow(i) || i.billable == 1))
      includeJobCostMaterialRowActual(i, false)
    )).reduce((acc, project) => {
      // Backup before non-T&M actual-only fix: includeRecipeCostMaterialRow(i) || i.billable == 1
      // Backup before T&M material markup: const total = parseFloat(project?.jobcost_total || (project?.vendor_price || project?.unit_cost || 0) * project?.jobcost_quantity || 0); return acc + total;
      return acc + getJobCostMaterialCost(project, isTMProject);
    }, 0).toFixed(2);
  // Backup before T&M material markup: }, [selectedProjects]);
  }, [selectedProjects, isTMProject]);

  const totalStockCost = useMemo(() => (
    stockProducts.reduce((sum, item) => {
      const cost = parseFloat(item.location_quantity || 0) * parseFloat(item.product_retail_price || 0);
      return sum + cost;
    }, 0)
  ), [stockProducts]);

  const totalStockCost1 = useMemo(() => (
    stockProducts.reduce((sum, item) => {
      if (!includeRecipeCostStockRow(item)) return sum;

      const cost = parseFloat(item.jobcost_total_cost || 0);
      return sum + cost;
    }, 0)
  ), [stockProducts]);

  const otherTotals = useMemo(() => getOtherEntriesTotals(otherEntries), [otherEntries]);
  const installLaborRowCostSum = (services || []).reduce((sum, s) => {
    if (Number(s?.labour_cost_service_id) !== 10) return sum;
    // Backup before non-T&M actual-only fix: includeRecipeCostServiceRow(s)
    if (!includeJobCostServiceRowActual(s, isTMProject)) return sum;
    return sum + parseFloat(s?.jobcost_total_cost || 0);
  }, 0);
  const manuallyAddedPayoutsTotal = useMemo(() => {
    return manuallyAddedPayouts?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
  }, [manuallyAddedPayouts]);
  const productionManualPayoutsTotal = useMemo(() => {
    // Backup before T&M category type fix: if (projectData?.project_category === 12) {
    if (isTMProject) {
      const manuallyAddedPayoutsForTM = allPayouts?.filter(
        // Backup before T&M production labor fix: p => (p.is_manually_added === 1 || p.is_manually_added === "1")
        // Backup before Install Labor request-service classification:
        // p => (p.is_manually_added === 1 || p.is_manually_added === "1") && !isJobCostInstallLaborPayout(p)
        p => (p.is_manually_added === 1 || p.is_manually_added === "1") && !isJobCostInstallLaborPayout(p, services)
      ) || [];
      return manuallyAddedPayoutsForTM.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
    }
    // Backup before non-T&M Install Labor production split:
    // return manuallyAddedPayoutsTotal;
    return manuallyAddedPayouts?.filter(
      p => !isJobCostInstallLaborPayout(p, services)
    )?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
  // Backup before T&M production labor fix: }, [projectData?.project_category, allPayouts, manuallyAddedPayoutsTotal]);
  // Backup before Install Labor request-service classification:
  // }, [isTMProject, allPayouts, manuallyAddedPayoutsTotal]);
  }, [isTMProject, allPayouts, manuallyAddedPayoutsTotal, manuallyAddedPayouts, services]);

  const laborCostTotalFromTimelogs = useMemo(() => {
    // Backup before T&M category type fix: const isTM = projectData?.project_category === 12;
    const isTM = Number(projectData?.project_category) === 12 || projectData?.job_category_name === 'Time & Material';
    const installLaborService = (services || []).find(s => Number(s.labour_cost_service_id) === 10);
    // OLD: const serviceRate = Number(installLaborService?.jobcost_unit_cost || installLaborService?.originalCost || installLaborService?.unit_cost || 0);
    const serviceRate = resolveInstallLaborServiceUnitCost(installLaborService, isTM);
    return (timelogsForCalc || []).reduce((sum, log) => sum + getLaborTimelogCost(log, isTM ? serviceRate : 0), 0);
  }, [timelogsForCalc, services, projectData?.project_category]);

  const installLaborMetrics = useMemo(() => {
    const logs = timelogsForCalc || [];
    let costTotal = 0;
    let billTotal = 0;
    
    // Find the Install Labor service rate for fallback
    // Backup before T&M category type fix: const isTM = projectData?.project_category === 12;
    const isTM = Number(projectData?.project_category) === 12 || projectData?.job_category_name === 'Time & Material';
    const installLaborService = (services || []).find(s => Number(s.labour_cost_service_id) === 10);
    // OLD: const serviceRate = Number(installLaborService?.jobcost_unit_cost || installLaborService?.originalCost || installLaborService?.unit_cost || 0);
    const serviceRate = resolveInstallLaborServiceUnitCost(installLaborService, isTM);
    const serviceBillRate = Number(installLaborService?.bill_unit_price || installLaborService?.unit_bill || 0);

    logs.forEach(log => {
      // const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      // const isDriver = log?.isDriver === 1 || log?.isDriver === '1' || log?.is_driver === 1 || log?.is_driver === '1';
      // const taskName = (log?.task_name || '').toString().trim().toUpperCase();
      // const isManual = log?.is_manually_added === 1 || log?.is_manually_added === '1';
      
      // const isDefaultLabor = sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver || !isManual));
      
      // if (isDefaultLabor) {
      //   // OLD: const fallbackR = isTM ? serviceRate : 0;
      //   const fallbackR = serviceRate;
      //   const fallbackBR = isTM ? serviceBillRate : 0;
      //   const computed = getRecipeCostLaborRowComputed(log, fallbackR, fallbackBR);
      //   costTotal += (Number(computed?.cost) || 0);
        
      //   const isBillable = log?.billable === 1 || log?.billable === true || log?.is_billable === 1 || log?.is_billable === true;
      //   if (isBillable) {
      //     billTotal += (Number(computed?.bill) || 0);
      //   }
      // }
      if (!isJobCostInstallLaborTimelog(log, services)) return;

      // OLD: const fallbackR = isTM ? serviceRate : 0;
      const fallbackR = serviceRate;
      const fallbackBR = isTM ? serviceBillRate : 0;
      const computed = getRecipeCostLaborRowComputed(log, fallbackR, fallbackBR);
      costTotal += (Number(computed?.cost) || 0);
      
      const isBillable = log?.billable === 1 || log?.billable === true || log?.is_billable === 1 || log?.is_billable === true;
      if (isBillable) {
        billTotal += (Number(computed?.bill) || 0);
      }
    });
    
    return {
      cost: costTotal,
      bill: billTotal
    };
  }, [timelogsForCalc, services, projectData?.project_category]);

  const additionalLaborCost1 = useMemo(() => (
    services.reduce((sum, service) => {
      // Backup before JobCost-aligned additional services fix:
      // if (!includeJobCostServiceRowActual(service, isTMProject) || service.recipe_id != null || service.labour_cost_service_id === 10) return sum;
      // const actual = getJobCostServiceActualCost(service, isTMProject);
      if (!includeRecipeCostServiceRow(service) || service.recipe_id != null || service.labour_cost_service_id === 10) return sum;
      const actual = parseFloat(service.jobcost_total_cost || 0);
      const expected = (parseFloat(service.originalCost) || 0) * (parseFloat(service.qty) || 0);
      // Backup before T&M category type fix: return sum + (projectData?.project_category !== 12 && expected > 0 ? Math.min(actual, expected) : actual);
      return sum + (!isTMProject && expected > 0 ? Math.min(actual, expected) : actual);
    }, 0)
  // Backup before T&M category type fix: ), [services, projectData?.project_category]);
  ), [services, isTMProject]);

  const productFormulaCost1 = useMemo(() => {
    const filtered = services?.filter(
      s => s?.recipe_id && s?.labour_cost_service_id !== 10 && includeJobCostServiceRowActual(s, isTMProject)
    );

    // OLD: parseFloat(s.jobcost_total_cost) || parseFloat(s.total_cost) — formula total_cost counted as actual before cost-card entry.
    const actualTotal = filtered.reduce((sum, s) => sum + getJobCostServiceActualCost(s, isTMProject), 0);
    const expectedTotal = filtered.reduce((sum, s) => sum + (parseFloat(s.originalCost || 0) * parseFloat(s.qty || 0)), 0);
    
    // Split into standard part and excess part if T&M
    // Backup before T&M category type fix: const standardPart = (projectData?.project_category !== 12 && expectedTotal > 0) ? Math.min(actualTotal, expectedTotal) : actualTotal;
    const standardPart = (!isTMProject && expectedTotal > 0) ? Math.min(actualTotal, expectedTotal) : actualTotal;
    const used = standardPart + productionManualPayoutsTotal;

    return used.toFixed(2);
  // Backup before T&M category type fix: }, [services, productionManualPayoutsTotal, projectData?.project_category]);
  }, [services, productionManualPayoutsTotal, isTMProject]);
  // Backup before T&M category type fix: const isTMProject = projectData?.project_category === 12;

  const productionTimelogLaborCost = useMemo(
    () => sumJobCostProductionTimelogLabor(timelogsForCalc, services, isTMProject),
    [timelogsForCalc, services, isTMProject]
  );
  const effectiveProductionLaborActual = useMemo(
    () =>
      resolveEffectiveProductionLabor({
        recipeLabor: productFormulaCost1,
        timelogLabor: productionTimelogLaborCost,
        tabLabor: laborTabLaborCost,
        isTM: isTMProject,
      }),
    [productFormulaCost1, productionTimelogLaborCost, laborTabLaborCost, isTMProject]
  );

  // OLD: excessLaborCost1 — install labor counted in labourExcess (graph Actual $130 vs Job Editing $76).
  // const excessLaborCost1 = useMemo(() => {
  //   let labourExpected = 0;
  //   let labourActual = 0;
  //   let productionExpected = 0;
  //   let productionActual = 0;
  //   // let otherExpected = 0;
  //   // let otherActual = 0;
  //   let otherExcess = 0;
  //   services.forEach((s) => {
  //     const isTaken = s.taken == 1 || s.billable == 1 || s.is_billable == 1;
  //     if (!isTaken) return;
  //     const unitCost = parseFloat(s.originalCost) || 0;
  //     const expected = parseFloat(unitCost * s.qty) || 0;
  //     const actual = parseFloat(s.jobcost_total_cost) || 0;
  //     if (+s.labour_cost_service_id === 10) {
  //       // install labour
  //       labourExpected += expected;
  //       labourActual += Math.max(actual, installLaborMetrics.cost);
  //     } else if (s.recipe_id && s.labour_cost_service_id !== 10) {
  //       // production labour
  //       productionExpected += expected;
  //       productionActual += actual;
  //     } else if (actual > expected) {
  //       // additional services / other
  //       otherExcess += actual - expected;
  //     }
  //   });
  //   const labourExcess = labourActual > labourExpected ? labourActual - labourExpected : 0;
  //   const productionExcess = productionActual > productionExpected ? productionActual - productionExpected : 0;
  //   // const otherExcess = otherActual > otherExpected ? otherActual - otherExpected : 0;
  //   if (projectData?.project_category === 12) return (0).toFixed(2);
  //   return (labourExcess + productionExcess + otherExcess + (excessHourlyPayoutAmount || 0) + (excessManualPayoutAmount || 0)).toFixed(2);
  // }, [services, excessHourlyPayoutAmount, excessManualPayoutAmount, installLaborMetrics, projectData?.project_category]);

  const installLaborCost1 = useMemo(() => {
    const installRows = services?.filter(s => Number(s?.labour_cost_service_id) === 10 && includeJobCostServiceRowActual(s, isTMProject)) || [];
    const manualRows = installRows.filter(s => s.jobcost_quantity != null && s.jobcost_quantity !== '' && parseFloat(s.jobcost_quantity) > 0);
    
    let actual = installLaborMetrics.cost;
    if (manualRows.length > 0) {
      // OLD: sumFromServices used parseFloat(s.jobcost_total_cost) only.
      const sumFromServices = installRows.reduce(
        (sum, s) => sum + resolveInstallLaborServiceLineCost(s, isTMProject),
        0
      );
      actual = Math.max(installLaborRowCostSum, sumFromServices);
    } else if (!isTMProject && installLaborMetrics.hours <= 0) {
      // OLD: fell back to SO install sell price when no cost-card entry or clock-in.
      actual = 0;
    }

    return actual.toFixed(2);
  }, [services, installLaborMetrics.cost, installLaborMetrics.hours, installLaborRowCostSum, isTMProject]);

  const excessLaborCost1 = useMemo(() => {
    let productionExpected = 0;
    let productionActual = 0;
    // let otherExpected = 0;
    // let otherActual = 0;
    let otherExcess = 0;

    services.forEach((s) => {
      const isTaken = s.taken == 1 || s.billable == 1 || s.is_billable == 1;
      if (!isTaken) return;

      // install labour
      if (+s.labour_cost_service_id === 10) return;

      const unitCost = parseFloat(s.originalCost) || 0;
      const expected = parseFloat(unitCost * s.qty) || 0;
      const actual = resolveJobCostServiceLineTotal(s, isTMProject);

      if (s.recipe_id && s.labour_cost_service_id !== 10) {
        // production labour
        productionExpected += expected;
        productionActual += actual;
      } else if (actual > expected) {
        // additional services / other
        otherExcess += actual - expected;
      }
    });

    const productionExcess =
      productionActual > productionExpected ? productionActual - productionExpected : 0;
    // const otherExcess = otherActual > otherExpected ? otherActual - otherExpected : 0;

    // Backup before T&M category type fix: if (projectData?.project_category === 12) return (0).toFixed(2);
    if (isTMProject) return (0).toFixed(2);
    return (
      productionExcess +
      otherExcess +
      (excessHourlyPayoutAmount || 0) +
      (excessManualPayoutAmount || 0)
    ).toFixed(2);
  }, [
    services,
    excessHourlyPayoutAmount,
    excessManualPayoutAmount,
    isTMProject,
    projectData?.project_category
  ]);

  const grandTotalCost = useMemo(() => {
    const servicesCost = services.reduce((sum, service) => {
      // OLD: if (Number(service.from_job) === 1) return sum;
      if (skipServiceOnExpectedBudgetList(service)) return sum;
      const quantity = parseFloat(service.qty || 0);
      // Backup before T&M graph expected Install Labor markup fix:
      // const unitCost = parseFloat(service.originalCost || service.unit_cost || 0);
      const unitCost = getChartExpectedServiceUnitCost(service, isTMProject);
      return sum + (quantity * unitCost);
    }, 0);
    return (
      parseFloat(materialPayOut || 0) +
      parseFloat(materialTotal || 0) +
      servicesCost +
      parseFloat(totalStockCost || 0)
    );
  }, [materialTotal, services, materialPayOut, totalStockCost, isTMProject]);

  const grandTotalCost1 = useMemo(() => {
    return (
      parseFloat(materialPayOut1 || 0) +
      parseFloat(materialTotal1 || 0) + 
      parseFloat(totalStockCost1 || 0) +
      parseFloat(additionalLaborCost1 || 0) +
      // OLD: parseFloat(productFormulaCost1 || 0) — missed workflow/subtask timelog production labor.
      parseFloat(effectiveProductionLaborActual || 0) +
      parseFloat(installLaborCost1 || 0) +
      parseFloat(excessLaborCost1 || 0) +
      Number(otherTotals?.cost || 0)
    );
  }, [
    materialTotal1,
    materialPayOut1,
    totalStockCost1,
    additionalLaborCost1,
    effectiveProductionLaborActual,
    installLaborCost1,
    excessLaborCost1,
    otherTotals
  ]);

  const compareExpectedTotalCost = Number(grandTotalCost || 0);
  const compareActualTotalCost = Number(grandTotalCost1 || 0);

  return (
    <Page title="Job Calculator">
      <Grid container spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ mb: 3.5 }}>
        <Grid item xs={12} md={6}>
          <Paper variant='outlined' sx={{ p: 1 }}>
            <JobCostChartSignwise
              actualSeries={[
                parseFloat(0),
                compareActualTotalCost,
              ]}
              expectedSeries={[
                parseFloat(0),
                compareExpectedTotalCost,
              ]}
              actualTotal={compareActualTotalCost}
              expectedTotal={compareExpectedTotalCost}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant='outlined' sx={{ p: 1 }}>
            <JobCostNotesPage
              logs={logs}
              projectData={projectData}
              setLogs={setLogs}
              fetchJobCostLogs={fetchJobCostLogs}
              requestId={requestId}
            />
          </Paper>
        </Grid>
      </Grid>
    </Page>
  );
}